import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { logisticsApi } from '../../network/apis/logistics';
import { Search, Truck } from '../../assets/icons';
import '../../assets/css/logistics/shipments.css';

const LogisticsShipments = () => {
    const [loading, setLoading] = useState(true);
    const [shipments, setShipments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedShipment, setSelectedShipment] = useState(null);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [updating, setUpdating] = useState(false);
    const [updateForm, setUpdateForm] = useState({
        status: '',
        location: '',
        description: ''
    });

    const statusOptions = [
        { value: 'all', label: 'All' },
        { value: 'PENDING', label: 'Pending' },
        { value: 'PICKED_UP', label: 'Picked Up' },
        { value: 'IN_TRANSIT', label: 'In Transit' },
        { value: 'DELIVERED', label: 'Delivered' },
        { value: 'CANCELLED', label: 'Cancelled' }
    ];

    const fetchShipments = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = {};
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            const response = await logisticsApi.getShipments(params);
            const data = response.data?.data || response.data || [];
            setShipments(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch shipments:', err);
            setError(err.response?.data?.message || 'Failed to load shipments');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchShipments();
    }, [fetchShipments]);

    const filteredShipments = shipments.filter(shipment => {
        const matchesSearch = 
            shipment.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            shipment.order?.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    const handleUpdateStatus = async (e) => {
        e.preventDefault();
        if (!selectedShipment || !updateForm.status) return;

        try {
            setUpdating(true);
            await logisticsApi.updateShipmentStatus(selectedShipment.id, updateForm);
            
            setShipments(prev => prev.map(s => 
                s.id === selectedShipment.id 
                    ? { ...s, status: updateForm.status }
                    : s
            ));
            
            setSuccess('Shipment status updated successfully');
            setShowUpdateModal(false);
            setSelectedShipment(null);
            setUpdateForm({ status: '', location: '', description: '' });
            
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update shipment status');
            setTimeout(() => setError(''), 3000);
        } finally {
            setUpdating(false);
        }
    };

    const openUpdateModal = (shipment, e) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedShipment(shipment);
        setUpdateForm({
            status: shipment.status,
            location: '',
            description: ''
        });
        setShowUpdateModal(true);
    };

    const getStatusClass = (status) => {
        return status?.toLowerCase().replace(' ', '_') || 'pending';
    };

    const getStatusDisplay = (status) => {
        const statusMap = {
            'PENDING': 'Pending',
            'PICKED_UP': 'Picked Up',
            'IN_TRANSIT': 'In Transit',
            'DELIVERED': 'Delivered',
            'CANCELLED': 'Cancelled',
            'FAILED': 'Failed'
        };
        return statusMap[status] || status;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-NG', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <div className="logistics-shipments">
            <header className="page-header">
                <h1>Shipments</h1>
            </header>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="filters-bar">
                <div className="search-input">
                    <img src={Search} alt="" />
                    <input
                        type="text"
                        placeholder="Search by tracking number or customer..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="status-filter">
                    {statusOptions.map(option => (
                        <button
                            key={option.value}
                            className={`filter-btn ${statusFilter === option.value ? 'active' : ''}`}
                            onClick={() => setStatusFilter(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="shipments-table">
                <div className="table-header">
                    <div>Tracking #</div>
                    <div>Customer</div>
                    <div>Status</div>
                    <div>Created</div>
                    <div>Est. Delivery</div>
                    <div>Actions</div>
                </div>

                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading shipments...</p>
                    </div>
                ) : filteredShipments.length === 0 ? (
                    <div className="empty-state">
                        <img src={Truck} alt="" />
                        <h3>No shipments found</h3>
                        <p>
                            {searchTerm || statusFilter !== 'all'
                                ? 'Try adjusting your filters'
                                : 'Shipments will appear here when orders are assigned to you'}
                        </p>
                    </div>
                ) : (
                    filteredShipments.map(shipment => (
                        <Link
                            key={shipment.id}
                            to={`/logistics/shipments/${shipment.id}`}
                            className="shipment-row"
                        >
                            <div data-label="Tracking #">
                                <span className="tracking-number">
                                    {shipment.tracking_number || `SHP-${shipment.id}`}
                                </span>
                            </div>
                            <div data-label="Customer" className="customer-info">
                                <span className="customer-name">
                                    {shipment.order?.customer_name || 'N/A'}
                                </span>
                                <span className="customer-address">
                                    {shipment.delivery_address?.city}, {shipment.delivery_address?.state}
                                </span>
                            </div>
                            <div data-label="Status">
                                <span className={`shipment-status ${getStatusClass(shipment.status)}`}>
                                    {getStatusDisplay(shipment.status)}
                                </span>
                            </div>
                            <div data-label="Created" className="date-col">
                                {formatDate(shipment.created_at)}
                            </div>
                            <div data-label="Est. Delivery" className="date-col">
                                {formatDate(shipment.estimated_delivery_date)}
                            </div>
                            <div data-label="Actions">
                                <button 
                                    className="action-btn"
                                    onClick={(e) => openUpdateModal(shipment, e)}
                                >
                                    Update
                                </button>
                            </div>
                        </Link>
                    ))
                )}
            </div>

            {/* Update Status Modal */}
            {showUpdateModal && selectedShipment && (
                <div className="modal-overlay" onClick={() => setShowUpdateModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Update Shipment Status</h2>
                        <p style={{ marginBottom: '1.5rem', color: 'var(--gray-600)' }}>
                            Tracking: <strong>{selectedShipment.tracking_number || `SHP-${selectedShipment.id}`}</strong>
                        </p>
                        
                        <form onSubmit={handleUpdateStatus}>
                            <div className="form-group">
                                <label>New Status *</label>
                                <select
                                    value={updateForm.status}
                                    onChange={(e) => setUpdateForm(prev => ({
                                        ...prev,
                                        status: e.target.value
                                    }))}
                                    required
                                >
                                    <option value="">Select status...</option>
                                    <option value="PENDING">Pending</option>
                                    <option value="PICKED_UP">Picked Up</option>
                                    <option value="IN_TRANSIT">In Transit</option>
                                    <option value="DELIVERED">Delivered</option>
                                    <option value="FAILED">Failed</option>
                                    <option value="CANCELLED">Cancelled</option>
                                </select>
                            </div>
                            
                            <div className="form-group">
                                <label>Current Location</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Lagos, Nigeria"
                                    value={updateForm.location}
                                    onChange={(e) => setUpdateForm(prev => ({
                                        ...prev,
                                        location: e.target.value
                                    }))}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Description / Notes</label>
                                <textarea
                                    rows="3"
                                    placeholder="Add any additional notes..."
                                    value={updateForm.description}
                                    onChange={(e) => setUpdateForm(prev => ({
                                        ...prev,
                                        description: e.target.value
                                    }))}
                                />
                            </div>
                            
                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={() => setShowUpdateModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-submit"
                                    disabled={updating || !updateForm.status}
                                >
                                    {updating ? 'Updating...' : 'Update Status'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LogisticsShipments;
